import moment from "moment";
import * as React from "react";
import { connect } from "react-redux";
import { match } from "react-router-dom";
import { showAlertBanner } from "../shared/alertBanner/actions";
import { ALERT_BANNER_TYPES } from "../shared/alertBanner/reducer";
import LoadingSpinner from "../shared/LoadingSpinner";
import selectorsToProps from "../utils/selectorsToProps";

import {
  loadAuditLog,
  LoadAuditLogActionType,
  loadHistoryAuditLog,
  LoadHistoryAuditLogActionType,
  resetAuditLog,
  ResetAuditLogActionType
} from "./actions";
import "./AuditLog.scss";
import { AuditLogTable } from "./AuditLogTable";
import { FormattedAuditEntityLog } from "./formattedAuditLog";
import {
  getAuditLog,
  getAuditLogError,
  getAuditLogLoaded,
  getFormattedHistoryAuditLog
} from "./selector";

import { getTimstamp } from "src/spread/model/spread/selector";

import AuditLogChangeComponent from "./AuditLogChange";
import { AuditLogData } from "./types";

import { AUDIT_LOG_DATE, AUDIT_LOG_DATE_FORMAT } from "../constants";

interface AuditLogProps {
  loadAuditLog: LoadAuditLogActionType;
  loadHistoryAuditLog: LoadHistoryAuditLogActionType;
  resetAuditLog: ResetAuditLogActionType;
  match: match<{ id: string }>;
  auditLogLoaded: boolean;
  auditLogError: boolean;
  auditLog: AuditLogData | any;
  historyAuditLog: FormattedAuditEntityLog;
  showAlertBanner: typeof showAlertBanner;
  uploadTime: string;
}

interface AuditLogState {
  page: number;
  order?: "asc" | "desc";
  orderBy?: string;
  activeTab: number;
  scope?: string;
}

export class AuditLogComponent extends React.Component<
  AuditLogProps,
  AuditLogState
> {
  public pageSize: number = 25;
  private isOld: boolean;

  constructor(props: AuditLogProps) {
    super(props);

    const { uploadTime } = this.props;

    const time = moment(AUDIT_LOG_DATE, AUDIT_LOG_DATE_FORMAT).valueOf();
    const uploadTimeInMillis = moment(
      uploadTime,
      AUDIT_LOG_DATE_FORMAT
    ).valueOf();

    this.isOld = time > uploadTimeInMillis;
    this.state = { page: 0, activeTab: 0 };
  }
  public componentDidMount() {
    if (this.isOld) {
      this.props.loadHistoryAuditLog({
        id: this.props.match.params.id
      });
    } else {
      this.props.loadAuditLog({
        id: this.props.match.params.id,
        from: 0,
        size: this.pageSize,
        order: this.state.order,
        orderBy: this.state.order
      });
    }
  }

  public componentWillUnmount() {
    this.props.resetAuditLog({});
  }

  public showErrorBanner = () => {
    this.props.showAlertBanner({
      type: ALERT_BANNER_TYPES.ERROR,
      message: "Error - Could Not Load Audit Log"
    });
  };

  public showAuditData = () => {
    const { auditLog, auditLogLoaded, historyAuditLog } = this.props;

    if (this.isOld && (!auditLogLoaded || !historyAuditLog.length)) {
      return <div className="AuditLog__no-entries">No entries.</div>;
    }

    return this.isOld ? (
      <>
        {historyAuditLog.map(auditLogChange => (
          <AuditLogChangeComponent
            auditLogChange={auditLogChange}
            key={auditLogChange.auditLogEntryID}
          />
        ))}
      </>
    ) : (
      <>
        {auditLog && (
          <AuditLogTable
            history={auditLog}
            updatePageFn={this.updatePage}
            updateSortFn={this.updateSort}
            updateTabFn={this.updateTab}
            page={this.state.page}
            activeTab={this.state.activeTab}
            orderBy={this.state.orderBy}
            order={this.state.order}
            pageSize={this.pageSize}
          />
        )}
      </>
    );
  };

  public render() {
    const { auditLogError, auditLogLoaded } = this.props;

    if (auditLogLoaded || auditLogError) {
      return (
        <div
          className={
            "AuditLog__container " +
            (this.isOld ? "AuditLog__history__container" : "")
          }
        >
          <div className="AuditLog__header__container">
            <div className="AuditLog__header">
              <div className="AuditLog__header__title">History</div>
            </div>
          </div>
          {auditLogError ? this.showErrorBanner() : this.showAuditData()}
        </div>
      );
    } else {
      return (
        <div className="AuditLog__container">
          <LoadingSpinner />
        </div>
      );
    }
  }

  public updateTab = (event: object, newValue: number) => {
    let scope = "all";

    if (newValue === 1) {
      scope = "statement";
    } else if (newValue > 1) {
      scope = this.periodOptions()[newValue - 2] || "all";
    }

    this.setState({
      activeTab: newValue,
      scope,
      page: 0,
      order: undefined,
      orderBy: undefined
    });

    this.props.loadAuditLog({
      id: this.props.match.params.id,
      from: 0,
      size: this.pageSize,
      order: undefined,
      orderBy: undefined,
      scope
    });
  };

  public updatePage = (params: any) => {
    const page = params.selected;

    this.setState({ page });

    this.props.loadAuditLog({
      id: this.props.match.params.id,
      from: page * this.pageSize,
      size: this.pageSize,
      order: this.state.order,
      orderBy: this.state.order,
      scope: this.state.scope
    });
  };

  private updateSort = (event, property) => {
    const orderBy = property;
    let order: "asc" | "desc" = "desc";

    if (this.state.orderBy === property && this.state.order === "desc") {
      order = "asc";
    }

    this.setState({ order, orderBy, page: 0 });

    this.props.loadAuditLog({
      id: this.props.match.params.id,
      from: 0,
      size: this.pageSize,
      order,
      orderBy,
      scope: this.state.scope
    });
  };

  private periodOptions = () => {
    return this.props.auditLog.periods.map(period => period.period);
  };
}

export default connect(
  selectorsToProps({
    auditLogLoaded: getAuditLogLoaded,
    auditLogError: getAuditLogError,
    auditLog: getAuditLog,
    historyAuditLog: getFormattedHistoryAuditLog,
    uploadTime: getTimstamp
  }),
  {
    loadAuditLog: loadAuditLog.action,
    loadHistoryAuditLog: loadHistoryAuditLog.action,
    showAlertBanner,
    resetAuditLog: resetAuditLog.action
  }
)(AuditLogComponent);



// WEBPACK FOOTER //
// ./src/auditLog/AuditLog.tsx